// PageSignup Component

import React, { Component } from 'react'
import PropTypes from 'prop-types'

import app from '../../../app.jsx'
import SignupContent from '../../signup/signup_content/signup_content.jsx'
import DialogTeamSelect from '../../team/dialog_team_select/dialog_team_select.jsx'
import { InvalidCredentials } from '../../../library/authentication/invalidCredentials'
import { AccountLocked } from '../../../library/authentication/accountLocked'
import { NoTeamAvailableError } from '../../../../shared/errors/NoTeamAvailableError.js'
import { signupExistingUser } from './signup'
import { upsertUserInStream } from '../page_chat/utils.js'

class TokenExpiredError extends Error { }

class PageSignup extends Component {
  constructor (props) {
    super(props)

    this.state = {
      teams: null,
      password: '',
      imageAvatar: null,
      imageAvatarUrl: null,
      imageDeleted: false,
      userTeam: null
    }

    this._references = {
      signupContent: React.createRef()
    }
  }

  static get propTypes () {
    return {
      token: PropTypes.string
    }
  }

  static get defaultProps () {
    return {
      token: ''
    }
  }

  componentDidMount () {
    this.getData()
  }

  render () {
    const { userTeam } = this.state

    if (!userTeam) {
      return null
    }

    return (
      <div className='page-signup'>
        {this.renderSignupContent()}
        {this.renderTeamSelect()}
      </div>
    )
  }

  renderSignupContent () {
    const { userTeam, userExists, imageAvatarUrl } = this.state
    const recaptchaEnabled = app.config.recaptcha.enabled

    return (
      <SignupContent
        ref={this._references.signupContent}
        email={userTeam.email}
        firstName={userTeam.firstName}
        lastName={userTeam.lastName}
        language={userTeam.language}
        showLogin={userExists}
        imageUrl={imageAvatarUrl}
        onUploadSuccess={this.onUploadSuccess.bind(this)}
        onUploadError={this.onUploadError.bind(this)}
        onDeleteImage={this.onUploadDelete.bind(this)}
        recaptchaEnabled={recaptchaEnabled}
        onSignup={this.onSignup.bind(this)}
      />
    )
  }

  onUploadSuccess (_file, message) {
    this.setState({
      imageAvatar: message.id,
      imageDeleted: false,
      imageAvatarUrl: message.url
    })
  }

  onUploadError (_file, _message) {
    if (this._references.signupContent.current) {
      this._references.signupContent.current.showSignupError(
        'common.form.uploadError'
      )
    }
    window.setTimeout(() => {
      if (this._references.signupContent.current) {
        this._references.signupContent.current.hideSignupError()
      }
    }, 2000)
  }

  onUploadDelete () {
    if (this.state.imageAvatar) {
      this.setState({
        imageDeleted: true,
        imageAvatar: null,
        imageAvatarUrl: null
      })
    } else {
      this.setState({
        imageDeleted: true,
        dataDate: new Date()
      })
    }
  }

  renderTeamSelect () {
    const { teams } = this.state

    if (teams && teams.length > 1) {
      return (
        <DialogTeamSelect
          teams={teams}
          onSelect={this.onTeamSelect.bind(this)}
        />
      )
    }

    return null
  }

  async getData () {
    try {
      const userTeam = await this._getUserTeam()
      const userTeams = await this._getUserTeams(userTeam)

      this.setState({ userTeam, userExists: userTeams.data.length > 0 })
    } catch (error) {
      if (error instanceof TokenExpiredError) {
        return app.redirect('/token/expired')
      } else {
        console.error(error)
        this._showError('Error in getting user information')
      }
    }
  }

  async _getUserTeam () {
    const userTeamRes = await app.service('/api/users/team').find({
      query: { inviteToken: this.props.token }
    })

    if (userTeamRes.data && userTeamRes.data.length > 0) {
      return userTeamRes.data[0]
    } else {
      throw new TokenExpiredError()
    }
  }

  async _getUserTeams (userTeam) {
    return app.service('/api/users/team').find({
      query: { userId: userTeam.userId, inviteToken: '' }
    })
  }

  _showError (err) {
    app.error(err)

    if (this._references.signupContent.current) {
      this._references.signupContent.current.showSignupError()
    }
  }

  async onSignup (userData) {
    const { userExists, userTeam } = this.state
    const { password, language } = userData

    if (!userData) {
      if (this._references.signupContent.current) {
        this._references.signupContent.current.showSignupError()
      }
      return
    }

    if (this._references.signupContent.current) {
      this._references.signupContent.current.hideSignupError()
    }

    this.setState({ password })

    try {
      let availableTeams, activeTeam
      if (userExists) {
        const loginResponse = await signupExistingUser(userTeam, password)

        availableTeams = loginResponse.availableTeams
        activeTeam = loginResponse.activeTeam

        await this.updateUser({ language })
      } else {
        await this.updateUser({ password, language })

        const loginResponse = await app.authentication.login({
          email: userTeam.email,
          password
        })

        await upsertUserInStream({
          userId: userTeam.userId,
          roles: userTeam.roles,
          firstName: userTeam.firstName,
          lastName: userTeam.lastName
        }, userTeam.teamId)

        availableTeams = loginResponse.availableTeams
        activeTeam = loginResponse.activeTeam
      }

      // If we have an active team, we redirect to the dashboard.
      if (activeTeam) {
        return app.redirect('/')
      }
      this.setState({ teams: availableTeams })
    } catch (error) {
      let message
      if (error instanceof InvalidCredentials) {
        message = 'user.login.msgInvalidValues'
      }

      if (error instanceof AccountLocked) {
        message = 'user.login.msgAccountLocked'
      }

      if (error instanceof NoTeamAvailableError) {
        message = 'user.login.msgInvalidTeams'
      }

      if (this._references.signupContent.current) {
        if (message) {
          this._references.signupContent.current.showError(message)
        } else {
          this._references.signupContent.current.showSignupError()
        }
      } else {
        console.error(error)
      }
    }
  }

  async updateUser ({ password, language }) {
    const { userTeam } = this.state
    const params = {
      query: { $client: { imageAvatar: this.state.imageAvatar } }
    }

    const updateObj = {
      inviteToken: this.props.token
    }

    if (language) {
      updateObj.language = language
    }

    if (password) {
      updateObj.password = password
    }

    app.service('/api/user/invite').timeout = 10000

    return app
      .service('/api/user/invite')
      .patch(userTeam.id, updateObj, params)
  }

  async onTeamSelect (teamId) {
    await app.teamContext.switchToTeamById({ userId: app.userId, teamId })
    return app.redirect('/')
  }
}

export default PageSignup
